import { useContext } from 'react'

import { getPreviousModule } from '@/api/modules'
import { JourneyContext } from '@/contexts/journey'
import { useCheckBoxModal } from '@/hooks/useCheckBoxModal'
import { useChoices } from '@/hooks/useChoices'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { CONSULTATION_LANDING_STEP } from '@/types/consultation'
import { Module, MODULE_TYPE } from '@/types/module'
import { getInitialStep } from '@/utils/step'
import { formatTrace } from '@/utils/traces'

interface UseStepProps {
  navigateToStep: (stepId: string) => void
  navigateBack: () => Promise<void>
  navigateToModule: (module: Module) => void
  returnToStep: (stepId: string) => void
}

export const useNavigate = (): UseStepProps => {
  const { user } = useUser()
  const { currentModule, setCurrentModule, currentStep, setCurrentStep } = useContext(JourneyContext)
  const { traces, setTraces, addTrace } = useTraces()
  const { opened: checkBoxModalOpened, close: closeCheckBoxModal } = useCheckBoxModal()
  const { removeChoiceByModuleAndStep } = useChoices()

  const navigateToStep = (stepId: string) => {
    if (!currentModule) throw new Error('No current module found')

    const step = currentModule?.steps[stepId]

    if (!step) throw new Error('Next step not found in current module')

    setCurrentStep(step)
    addTrace(currentModule.id, step?.id)
  }

  const returnToStep = (stepId: string) => {
    if (!currentModule) throw new Error('No current module found')
    if (!currentStep) throw new Error('No current step found')

    const step = stepId === CONSULTATION_LANDING_STEP.id ? CONSULTATION_LANDING_STEP : currentModule?.steps[stepId]

    if (!step) throw new Error('Step not found in current module')

    const stepTraceId = traces.findIndex((trace) => trace.stepId === stepId)

    removeChoiceByModuleAndStep(currentModule.id, currentStep.id)

    setCurrentStep(step)
    setTraces((prev) => prev.slice(0, stepTraceId + 1))
  }

  const navigateToModule = (module: Module) => {
    if (!user) throw new Error('No user found')

    const currentStep = getInitialStep({ module, user })
    if (!currentStep) throw new Error('No initial step found')

    setCurrentModule(module)
    if (module.type === MODULE_TYPE.DEFAULT) {
      setCurrentStep(currentStep)
      setTraces((prev) => [...prev, formatTrace(module.id, currentStep.id, prev)])
    }
  }

  const navigateBack = async () => {
    if (checkBoxModalOpened) return closeCheckBoxModal()

    const currentTrace = traces[traces.length - 1]
    const previousTrace = traces[traces.length - 2]

    // Navigate to previous step if not first of module
    if ((currentTrace && previousTrace && !currentTrace.isFirstOfModule) || previousTrace?.stepId === CONSULTATION_LANDING_STEP.id)
      return returnToStep(previousTrace.stepId)

    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const { module } = await getPreviousModule({
      uniqueId: user?.data?.uniqueId
    })

    if (!module) throw new Error('No module found')

    setCurrentModule(module)

    setCurrentStep(previousTrace ? module.steps[previousTrace.stepId] : getInitialStep({ module, user }))
    setTraces((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev))
  }

  return {
    navigateToStep,
    navigateBack,
    navigateToModule,
    returnToStep
  }
}
